import DateTime from 'common/components/DateTime/DateTime';
import lodash from 'lodash';

import { DOA_TYPE_TASK, EVENT_TYPE_TASK, SDFA_TYPE_TASK } from 'common/util/taskHelper';
import { daysSinceNowUTC, daysBetweenDatesUTC } from 'common/util/dateTime';
import { 
    getRecipientDisplayName,
    getTasksRecipientsWithPendingSignatures,
    getTasksRecipientsWithCompletedSignatures
} from 'common/util/taskHelper';

import { getDateInInstitutionTZ } from 'common/util/timezoneFormat';

export let pendingTaskFilterOption = {
    sortBy : [
        { key: 'Days Since Assigned', value: 'daysSinceAssigned', format :'number' },
        { key: 'Task Title', value: 'title', format : 'string' },
        { key: 'Document Type', value: 'document.documentType.displayName', format : 'string' },
        { key: 'Assigned By', value: 'authorDisplayName', format : 'string' },
        { key: 'Completed  Actions', value: 'completePercentage', format : 'number' },
    ],
    sortOrder : [
        { key: 'Descending', value: -1 },
        { key: 'Ascending', value: 1 },
    ]
};


export let completedTaskFilterOption = {
    sortBy : [
        { key: 'Completion Date', value: 'endDate', format : 'date' },
        { key: 'Task Title', value: 'title', format : 'string' },
        { key: 'Document Type', value: 'document.documentType.displayName', format : 'string' },
        { key: 'Assigned By', value: 'authorDisplayName', format : 'string' },
        { key: 'Completed  Actions', value: 'finishedTask', format : 'number' },
    ],
    sortOrder : [
        { key: 'Descending', value: -1 },
        { key: 'Ascending', value: 1 },
    ]
};

export function pendingTaskTableColumns(task){
    let columnHeaders = [{
        header: 'Task Title',
        accessor: 'title',
        styles: {
            width: '40%',
        },
        component: task => {
            return(
                <div>
                    <span style={{fontSize : '16px', fontWeight : '600', color: '#0056ad'}}>{task.title}</span>
                </div>
            );
        }
    }, {
        header: 'Document Type',
        accessor: 'document.documentType.displayName',
        styles: {
            width: '22%',
        }
    }, {
        header: 'Assigned By',
        accessor: 'authorDisplayName',
        styles: {
            width: '22%',
            maxWidth: '12rem',
            wordBreak: 'break-word',
        }
    }, {
        header: 'Completed Actions',
        accessor: 'completePercentage',
        styles: {
            width: 'auto',
            margin: 'auto',
            overflow: 'visible',
            
        },
        component: task => {

            return(
                <div>
                    <span style={{color :'#0056ad', fontSize : '16px', fontWeight: '600'}}>{task.completedInstances}</span>
                    <span style={{color :'#8FA0B3', fontSize : '16px', fontWeight: '600'}}> of {parseInt(task.completedInstances)+ parseInt(task.activeInstances)}</span>
                </div>
            );
        }
    }, {
        header: 'Days Since Assigned',
        accessor: 'daysSinceAssigned',
        styles: {
            width: 'auto',
            margin: 'auto',
            overflow: 'visible',
        },
        component: task => {
            return(
                <div>
                    <span style={{color :'#0056ad', fontSize : '16px', fontWeight: '600'}}>{task.daysSinceAssigned}</span>
                </div>
            );
        }
    }];

    return columnHeaders;   
}

export function completedTaskTableColumns (task) {
    let columnHeaders = [{
        header: 'Task Title',
        accessor: 'title',
        styles: {
            width: '40%',
        },
        component: task => {
            return(
                <div>
                    <span style={{fontSize : '16px', fontWeight : '600', color: '#0056ad'}}>{task.title}</span>
                </div>
            );
        }
    }, {
        header: 'Document Type',
        accessor: 'document.documentType.displayName',
        styles: {
            width: '22%',   
        }
    }, {
        header: 'Assigned By',
        accessor: 'authorDisplayName',
        styles: {
            width: '22%',
            maxWidth: '12rem',
            wordBreak: 'break-word',
        }
    }, {
        header: 'Completed Actions',
        accessor: 'finishedTask',
        styles: {
            width: 'auto',
            marginLeft: 'auto',
            overflow: 'visible',
        },
        component: task => {
            return(
                <div>
                    <span style={{fontSize : '16px', fontWeight : '600', color: '#0056ad'}}>{task.finishedTask}</span>
                </div>
            );
        }
    }, {
        header: 'Completion Date',
        accessor: 'endDate',
        styles: {
            width: 'auto',
            marginLeft: 'auto',
            overflow: 'visible',
        },
        component: task => {
            return(
                <DateTime 
                    time={true}
                    style={{fontSize : '16px', color: '#0056ad',fontWeight: '600'}}
                    format={'MMM D, YYYY'}
                    value={task.endDate} 
                />
            );
        }
    }];

    return columnHeaders;
}

function formatPendingSignatures(id, documentType, recipients, sentDate, taskType, activeTasks = 1,  totalTasks = 1){
    const daysSinceNow = daysSinceNowUTC(sentDate);

    return {
        id,
        documentType,
        recipient : parseInt(totalTasks) === 1 ? getRecipientDisplayName(recipients[0]) : `${activeTasks} of ${totalTasks} Recipients Incomplete` ,
        sentDate: sentDate,
        priority: daysSinceNow,
        detailedRecipients : recipients || [],
        taskType,
        activeTasks,
        totalTasks,
    };
}

export const getPendingBinderSignatures = (state) => {
    const pendingTasks = lodash.get(state, 'binder.tasks.pending', []);
    const pendingEvents = lodash.get(state, 'binder.events.pending', []);
    const pendingDOAs = lodash.get(state, 'binder.delegations.pending', []);
    const institutionTimezone = lodash.get(state, 'me.institutionTimezone');

    const recipientsForPendingTasks =  getTasksRecipientsWithPendingSignatures(pendingTasks);

    const formattedPendingTasks = recipientsForPendingTasks.map( task => formatPendingSignatures(
        task.id,
        task.document ? task.document.documentType.displayName : '',
        task.recipients,
        getDateInInstitutionTZ(institutionTimezone, task.sentDate),
        SDFA_TYPE_TASK,
        task.activeInstances,
        task.activeTasks.length,
    ));

    const formattedPendingEvents = pendingEvents.map( pendingEvent => formatPendingSignatures(
        pendingEvent.id,
        pendingEvent.event.eventType,
        [pendingEvent.requestedFrom],
        getDateInInstitutionTZ(institutionTimezone, pendingEvent.sentDate),
        EVENT_TYPE_TASK,
    ));

    const formattedPendingDOAs = pendingDOAs.map(pendingDOA => formatPendingSignatures(
        lodash.get(pendingDOA, 'id'),
        lodash.get(pendingDOA, 'delegation.user.displayName') +
            ' as ' +
            lodash.get(pendingDOA, 'delegation.rolesManagement.name'),
        [lodash.get(pendingDOA, 'requestedFrom')],
        getDateInInstitutionTZ(institutionTimezone, lodash.get(pendingDOA, 'sentDate')),
        DOA_TYPE_TASK,
    ));

    const pendingSortedBySentDate = [...formattedPendingTasks, ...formattedPendingEvents, ...formattedPendingDOAs].sort( (a, b) => {
        return new Date(a.sentDate) - new Date(b.sentDate);
    });

    return pendingSortedBySentDate;
};

function formatCompletedSignatures(id, documentType, recipients, sentDate, endDate, taskType, completedTasks = 1, totalTasks = 1){
    const daysToComplete = Math.abs(daysBetweenDatesUTC(sentDate, endDate));

    return {
        id,
        documentType,
        recipient : recipients.length === 1 ? getRecipientDisplayName(recipients[0]) : `${completedTasks} Recipients` ,
        completedDate: endDate,
        detailedRecipients : recipients || [],
        daysToComplete,
        taskType,
        completedTasks,
        totalTasks,
    };
}

export const getCompletedBinderSignatures = (state) => {
    const completedTasks = lodash.get(state, 'binder.tasks.completed', []);
    const completedEvents = lodash.get(state, 'binder.events.completed', []);
    const completedDOAs = lodash.get(state, 'binder.delegations.completed', []);
    const institutionTimezone = lodash.get(state, 'me.institutionTimezone');

    const recipientsForCompletedTasks = getTasksRecipientsWithCompletedSignatures(completedTasks);
    const formattedCompletedTasks = recipientsForCompletedTasks.map(completedTask => formatCompletedSignatures(
        completedTask.id,
        completedTask.document ? completedTask.document.documentType.displayName : '',
        completedTask.recipients,
        getDateInInstitutionTZ(institutionTimezone, completedTask.sentDate),
        getDateInInstitutionTZ(institutionTimezone, completedTask.endDate),
        SDFA_TYPE_TASK,
        completedTask.recipients.length,
        completedTask.tasks.length,
    ));

    const formattedCompletedEvents = completedEvents.map(completedEvent => formatCompletedSignatures(
        completedEvent.id,
        completedEvent.event.eventType,
        [completedEvent.requestedFrom],
        getDateInInstitutionTZ(institutionTimezone, completedEvent.sentDate),
        getDateInInstitutionTZ(institutionTimezone, completedEvent.signedDate),
        EVENT_TYPE_TASK,
    ));

    const formattedCompletedDOAs = completedDOAs.map(completedDOA => formatCompletedSignatures(
        lodash.get(completedDOA, 'id'),
        lodash.get(completedDOA, 'delegation.user.displayName') +
            ' as ' +
            lodash.get(completedDOA, 'delegation.rolesManagement.name'),
        [lodash.get(completedDOA, 'requestedFrom')],
        getDateInInstitutionTZ(institutionTimezone, lodash.get(completedDOA, 'sentDate')),
        getDateInInstitutionTZ(institutionTimezone, lodash.get(completedDOA, 'signedDate')),
        DOA_TYPE_TASK,
    ));
    
    const completedSortedByCompletedDate = [...formattedCompletedTasks, ...formattedCompletedEvents, ...formattedCompletedDOAs].sort( (a, b) => {
        return new Date(b.completedDate) - new Date(a.completedDate);
    });

    return completedSortedByCompletedDate;
};